import { Check, Search } from 'lucide-react';
import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import type { FieldBaseProps } from './types';

export type ComboboxOption = {
  value: string;
  label: string;
  description?: string;
};

type Props<T extends FieldValues> = FieldBaseProps<T> & {
  options: ComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  /** Use a modal popover (required inside a Radix Dialog so selecting an item
   * doesn't fall through to the control behind it). */
  modal?: boolean;
};

/**
 * Searchable single-select bound to react-hook-form. Renders the selected value
 * as a chip with a search affordance (Figma create-order combobox).
 */
export function FormComboboxField<T extends FieldValues>({
  name,
  label,
  description,
  required,
  disabled,
  className,
  options,
  placeholder,
  searchPlaceholder,
  emptyText,
  modal = false,
}: Props<T>) {
  const { control } = useFormContext<T>();
  const [open, setOpen] = useState(false);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const selected = options.find((option) => option.value === field.value);
        return (
          <FormItem className={className}>
            {label ? (
              <FormLabel>
                {label}
                {required ? <span aria-hidden="true"> *</span> : null}
              </FormLabel>
            ) : null}
            <Popover open={open} onOpenChange={setOpen} modal={modal}>
              <PopoverTrigger asChild>
                <FormControl>
                  <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className="flex h-9 w-full items-center justify-between gap-2 rounded-[10px] border border-input bg-white px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {selected ? (
                      <span className="inline-flex items-center rounded-[8px] bg-accent px-2 py-0.5 text-sm font-medium text-foreground">
                        {selected.label}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">{placeholder}</span>
                    )}
                    <Search className="size-4 shrink-0 opacity-60" />
                  </button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder={searchPlaceholder} />
                  <CommandList>
                    <CommandEmpty>{emptyText}</CommandEmpty>
                    <CommandGroup>
                      {options.map((option) => (
                        <CommandItem
                          key={option.value}
                          value={`${option.label} ${option.description ?? ''}`}
                          onSelect={() => {
                            field.onChange(option.value);
                            setOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 size-4',
                              option.value === field.value ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          <div className="flex flex-col">
                            <span>{option.label}</span>
                            {option.description ? (
                              <span className="text-xs text-muted-foreground">
                                {option.description}
                              </span>
                            ) : null}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {description ? <FormDescription>{description}</FormDescription> : null}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
